import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { CreditCard, Plus, Building, Smartphone, Copy } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { EmptyState } from "@/components/ui/EmptyState";
import type { PaymentGateway } from "@/types/payment";

interface Form {
  name: string;
  provider_type: string;
  account_details: string;
  instructions: string;
  is_active: string;
  display_order: number;
}

export function PaymentGatewaysPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-payment-gateways"],
    queryFn: () => get<PaymentGateway[]>("/admin/payment-gateways"),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Form>({
    defaultValues: { provider_type: "mfs", is_active: "true", display_order: 0 }
  });

  const create = useMutation({
    mutationFn: (d: Form) => {
      const formData = new FormData();
      formData.append("name", d.name);
      formData.append("provider_type", d.provider_type);
      formData.append("account_details", d.account_details);
      formData.append("instructions", d.instructions);
      formData.append("is_active", d.is_active);
      formData.append("display_order", String(d.display_order));
      
      const fileInput = document.getElementById("gw-logo") as HTMLInputElement;
      if (fileInput?.files?.[0]) formData.append("logo", fileInput.files[0]);

      return post("/admin/payment-gateways", formData, { headers: { "Content-Type": "multipart/form-data" } });
    },
    onSuccess: () => {
      toast.success("Payment Gateway created!");
      qc.invalidateQueries({ queryKey: ["admin-payment-gateways"] });
      reset();
      setOpen(false);
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Payment Gateways</h1>
          <p className="text-sm text-surface-500 mt-1">Manage dynamic payment methods shown at checkout.</p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4"/>} onClick={() => setOpen(true)}>Add Gateway</Button>
      </header>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (data ?? []).length === 0 ? (
             <EmptyState icon={CreditCard} title="No Payment Gateways" description="Add methods like bKash or Bank Transfer." />
          ) : (
            <Table>
              <THead>
                <TR><TH>Logo</TH><TH>Name</TH><TH>Provider</TH><TH>Account Details</TH><TH>Order</TH><TH>Status</TH></TR>
              </THead>
              <tbody>
                {data!.map(gw => (
                  <TR key={gw.id}>
                    <TD>
                      {gw.logo ? <img src={gw.logo} alt={gw.name} className="h-8 object-contain" /> : (
                        gw.provider_type === "mfs" ? <Smartphone className="h-6 w-6 text-brand-500"/> : <Building className="h-6 w-6 text-surface-500"/>
                      )}
                    </TD>
                    <TD className="font-bold">{gw.name}</TD>
                    <TD className="uppercase text-xs font-semibold text-surface-500">{gw.provider_type}</TD>
                    <TD className="font-mono text-sm">{gw.account_details}</TD>
                    <TD>{gw.display_order}</TD>
                    <TD><StatusPill status={gw.is_active ? "active" : "inactive"} /></TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="New Payment Gateway" size="lg">
        <form onSubmit={handleSubmit(d => create.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Method Name" placeholder="e.g. bKash Personal" error={errors.name?.message} {...register("name", { required: "Required" })} />
            <Select label="Provider Type" options={[{value:"mfs", label:"Mobile Banking"}, {value:"bank", label:"Bank Transfer"}, {value:"crypto", label:"Crypto"}]} {...register("provider_type")} />
          </div>
          
          <Input label="Account Number / Details" placeholder="017XXXXXX" error={errors.account_details?.message} {...register("account_details", { required: "Required" })} />
          
          <div>
            <label className="block text-sm font-medium text-surface-800 mb-1.5">Instructions for Customer</label>
            <textarea {...register("instructions")} className="w-full h-20 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500" placeholder="E.g. Send money and put transaction ID below..." />
          </div>

          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-surface-800 mb-1.5">Gateway Logo (Optional)</label>
              <input type="file" id="gw-logo" accept="image/*" className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700" />
            </div>
            <Input label="Display Order" type="number" {...register("display_order")} />
          </div>

          <Select label="Status" options={[{value:"true", label:"Active"}, {value:"false", label:"Inactive"}]} {...register("is_active")} />

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" loading={create.isPending}>Save Gateway</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}